import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, FileDown, ShieldAlert, ShieldCheck, Pencil, Activity, ArrowLeft, ListChecks } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import * as takata from '../services/takataService';
import { VerificaTakata, ESITO_LABEL } from '../services/takataService';
import DatiDocumento from '../components/takata/DatiDocumento';
import IstruzioniEstensione from '../components/takata/IstruzioniEstensione';
import SegnalaProblema from '../components/takata/SegnalaProblema';
import LogoMarca from '../components/takata/LogoMarca';

function badgeClass(esito: string) {
  if (esito === 'richiamo' || esito === 'stop_drive') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  if (esito === 'non_soggetto') return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  if (esito === 'fonte_non_disponibile') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
}

const TakataRegistroPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<VerificaTakata[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [esito, setEsito] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Riga con il pannello dei dati aperto: una alla volta, altrimenti l'elenco diventa
  // una sequenza di moduli e si perde di vista quale verifica si sta completando.
  const [apertaId, setApertaId] = useState<string | null>(null);
  // Chi può usare il modulo di verifica sul sito (per provare i portali): a tutti gli altri
  // il pulsante non compare, perché la verifica si fa con l'estensione.
  const [moduloWeb, setModuloWeb] = useState(false);
  useEffect(() => {
    let vivo = true;
    takata.getAccessoModuloWeb()
      .then((r) => { if (vivo) setModuloWeb(!!r.consentito); })
      .catch(() => { /* nel dubbio non si mostra */ });
    return () => { vivo = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await takata.getRegistro({ page, esito: esito || undefined, search: search || undefined });
      setItems(Array.isArray(r.items) ? r.items : []);
      setTotalPages(r.totalPages || 1);
    } catch (e) {
      toast.error('Errore nel caricamento del registro');
    } finally {
      setLoading(false);
    }
  }, [page, esito, search, toast]);

  useEffect(() => { load(); }, [load]);

  // Download automatico se aperti dall'estensione con ?scarica=<id>
  // Si scarica sempre il certificato: quando l'esito e' coinvolgente contiene gia'
  // la dichiarazione di presa visione come pagina successiva (documento unico).
  const [searchParams, setSearchParams] = useSearchParams();
  const autoDone = useRef(false);
  useEffect(() => {
    const id = searchParams.get('scarica');
    if (!id || autoDone.current) return;
    autoDone.current = true;
    (async () => {
      try {
        const r = await takata.getVerifica(id);
        if (r.verifica && !takata.haCertificato(r.verifica.esito)) {
          // Marca fuori dall'elenco del Ministero: nessun certificato, si dice l'esito.
          toast.success(`${r.verifica.marca}: ${takata.TESTO_NON_SOGGETTO}`);
        } else if (r.verifica) {
          await takata.scaricaCertificato(r.verifica);
        }
      } catch (e) {
        toast.error('Documento non scaricato: aprilo dal registro');
      } finally {
        searchParams.delete('scarica');
        searchParams.delete('tipo');
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apertura di una verifica specifica con ?v=<id>: è il pulsante «Apri la scheda» del
  // pannello dell'estensione, premuto subito dopo una verifica per completarne i dati
  // (acquirente, pratica, corrispettivo). Senza questo, quel pulsante lasciava l'operatore
  // davanti a un elenco generico in cui doveva ricercarsi il telaio a mano.
  // La riga è appena stata creata, quindi è la prima dell'elenco: se non fosse fra quelle
  // caricate non accade nulla di male, resta semplicemente chiusa.
  useEffect(() => {
    const id = searchParams.get('v');
    if (!id) return;
    setApertaId(id);
    searchParams.delete('v');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (d: string) => {
    const dt = new Date(d);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Risalita all'area riservata: queste pagine non hanno l'intestazione del sito, e
            chi arriva qui dal link condiviso apre una scheda nuova — senza questo pulsante
            non avrebbe modo di raggiungere il resto dell'area riservata. */}
        <button onClick={() => navigate('/area-riservata')} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Area riservata
        </button>

        {/* Testata con foto (Higgsfield, 22/10/2026): titolo e azioni sopra la parte rossa
            dell'immagine, che a destra mostra la verifica conclusa accanto al volante. Su
            telefono la foto si sposta a destra e la sfumatura tiene leggibile il testo. */}
        <div className="relative overflow-hidden rounded-2xl mb-5 bg-red-950 sm:min-h-[200px]">
          <img
            src="/images/takata/registro-hero.webp"
            srcSet="/images/takata/registro-hero-800.webp 800w, /images/takata/registro-hero.webp 1600w"
            sizes="(min-width: 896px) 896px, 100vw"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover object-right"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-950/95 via-red-950/70 to-transparent sm:via-red-950/40" />
          <div className="relative p-5 sm:p-7 max-w-md text-white">
            <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-white/70 mb-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Servizio per gli associati
            </p>
            <h1 className="text-lg font-bold">Verifica richiamo airbag Takata</h1>
            <p className="text-xs text-white/80">Obbligatoria prima di ogni pratica, dal 2 luglio 2026</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {/* Verifica multipla: per recuperare in blocco le pratiche già lavorate. */}
              <button onClick={() => navigate('/area-riservata/takata/multipla')}
                className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-white text-red-900 hover:bg-red-50 shadow">
                <ListChecks className="w-4 h-4" /> Verifica multipla
              </button>
              <button onClick={() => navigate('/area-riservata/takata/stato-portali')}
                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white backdrop-blur-sm">
                <Activity className="w-4 h-4" /> Stato portali
              </button>
              {/* Il modulo di verifica sul sito è riservato a chi prova i portali: per tutti gli
                  altri la verifica si fa con l'estensione, e questo pulsante non compare. */}
              {moduloWeb && (
                <button onClick={() => navigate('/area-riservata/takata/verifica')}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white backdrop-blur-sm">
                  <ShieldCheck className="w-4 h-4" /> Modulo
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Prima cosa che si vede: come si installa e come si lavora. Il riquadro si
            riduce a una riga quando l'estensione è già installata. */}
        <IstruzioniEstensione />

        {/* Chi incontra un portale che non funziona è qui, appena dopo la verifica andata
            storta: prima doveva sapere che esisteva un'altra pagina apposta. */}
        <div className="mb-5">
          <SegnalaProblema />
        </div>

        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Verifiche effettuate</h2>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Cerca per telaio, targa o protocollo"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
          </div>
          <select value={esito} onChange={(e) => { setPage(1); setEsito(e.target.value); }}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
            <option value="">Tutti gli esiti</option>
            <option value="non_coinvolto">Non coinvolto</option>
            <option value="altri_richiami">Altri richiami</option>
            <option value="richiamo">Coinvolto</option>
            <option value="stop_drive">Stop Drive</option>
            <option value="non_soggetto">Non soggetto</option>
            <option value="fonte_non_disponibile">Fonte non disponibile</option>
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nessuna verifica registrata.</p>
        ) : (
          <div className="space-y-2">
            {items.map((v) => {
              const coinvolto = takata.isCoinvolto(v.esito);
              const aperta = apertaId === v._id;
              // Bordo colorato a sinistra: in un elenco lungo dice l'esito prima ancora che
              // si legga, e distingue le righe che richiedono un'azione.
              return (
                <div key={v._id} className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${coinvolto ? 'border-l-red-500' : 'border-l-blue-400'}`}>
                  <div className="p-3 flex items-center gap-3">
                    <LogoMarca nome={v.marca} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {coinvolto
                          ? <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0" />
                          : <ShieldCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                        <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{v.vin}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{v.marca}</span>
                        <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${badgeClass(v.esito)}`}>{ESITO_LABEL[v.esito]}</span>
                        {v.numeroPraticaSTA && (
                          <span className="text-2xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            pratica {v.numeroPraticaSTA}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {fmt(v.createdAt)} · {v.protocollo}
                        {/* Chi l'ha eseguita: con i dipendenti dello studio il titolare
                            deve poter distinguere le proprie verifiche da quelle altrui. */}
                        {v.operatore ? ` · eseguita da ${v.operatore}` : ''}
                        {/* Solo se incassato qualcosa: uno «0,00 €» su ogni riga sarebbe
                            rumore, visto che la maggior parte delle verifiche non ha
                            corrispettivo. */}
                        {v.corrispettivo?.importo ? ` · ${v.corrispettivo.importo.toFixed(2).replace('.', ',')} €` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {/* Con l'estensione la pagina di verifica non si vede nemmeno: a esito
                          catturato il certificato si scarica da sé e si finisce qui. È quindi
                          da qui che si devono poter completare pratica e dati dell'acquirente. */}
                      <button
                        onClick={() => setApertaId(aperta ? null : v._id)}
                        title={aperta ? 'Chiudi' : 'Completa i dati del documento'}
                        className={`p-2 rounded-lg ${aperta ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {takata.haCertificato(v.esito) && (
                        <button onClick={() => takata.scaricaCertificato(v)} title="Certificato" className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                          <FileDown className="w-4 h-4" />
                        </button>
                      )}
                      {coinvolto && (
                        <button onClick={() => takata.scaricaDichiarazione(v)} title="Dichiarazione" className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                          <FileDown className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {aperta && (
                    <div className="px-3 pb-3 pt-1 border-t border-gray-200 dark:border-gray-700">
                      <DatiDocumento
                        verifica={v}
                        onSaved={(agg) => setItems((prec) => prec.map((x) => (x._id === agg._id ? agg : x)))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 text-gray-700 dark:text-gray-300">Precedente</button>
            <span className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400">{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 text-gray-700 dark:text-gray-300">Successivo</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TakataRegistroPage;
